import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthRoute } from './auth/auth-route';
import { CommonLayout } from '@components/layout/containers/CommonLayout';
import { useRouteChangeHandler } from '@hooks/useRouteChangeHandler';
import { useExternalPointDetection } from '@hooks/useExternalPointDetection';
import { MyPageLayout } from '@components/layout/MyPageLayout';
import { RefNavigate } from '@components/ui/RefNavigate';
import { ROUTE_CONFIGS } from '@configs/routes';
import { SpinnerProvider } from '@components/ui/progressbar/SpinnerProvider';

// Lazy loaded pages - Common
const HomePage = lazy(() => import('./pages/index').then((m) => ({ default: m.HomePage })));
const SignInPage = lazy(() => import('./pages/sign-in').then((m) => ({ default: m.SignInPage })));
const SignUpPage = lazy(() => import('./pages/sign-up').then((m) => ({ default: m.SignUpPage })));
const FindIdPage = lazy(() => import('./pages/find-id').then((m) => ({ default: m.FindIdPage })));
const ResetPasswordPage = lazy(() => import('./pages/reset-password').then((m) => ({ default: m.ResetPasswordPage })));
const GoogleCallbackPage = lazy(() =>
  import('./pages/auth/google-callback').then((m) => ({ default: m.GoogleCallbackPage })),
);
const SignProvisionPage = lazy(() => import('./pages/sign-provision').then((m) => ({ default: m.SignProvisionPage })));
const SignCompletePage = lazy(() => import('./pages/sign-complete').then((m) => ({ default: m.SignCompletePage })));
const EyeTDownloadPage = lazy(() => import('./pages/eyet-download').then((m) => ({ default: m.EyeTDownloadPage })));
const MarketPlacePage = lazy(() => import('./pages/products/index').then((m) => ({ default: m.MarketPlacePage })));
const CommunityAnalysisRequestPage = lazy(() =>
  import('./pages/analysis-request').then((m) => ({ default: m.CommunityAnalysisRequestPage })),
);
const CommunityInquiryPage = lazy(() => import('./pages/inquiry').then((m) => ({ default: m.CommunityInquiryPage })));
const ProductDetailPage = lazy(() => import('./pages/products/[id]').then((m) => ({ default: m.ProductDetailPage })));
const ProductIntroPage = lazy(() => import('./pages/product-intro').then((m) => ({ default: m.ProductIntroPage })));
const EducationCoursePage = lazy(() =>
  import('./pages/education-course').then((m) => ({ default: m.EducationCoursePage })),
);
const ServicePage = lazy(() => import('./pages/service').then((m) => ({ default: m.ServicePage })));
const TermsPage = lazy(() => import('./pages/terms').then((m) => ({ default: m.TermsPage })));
const ExpertCenterPage = lazy(() => import('./pages/expert-center').then((m) => ({ default: m.ExpertCenterPage })));
const ExternalLoginPage = lazy(() => import('./pages/external-login').then((m) => ({ default: m.ExternalLoginPage })));

// Lazy loaded pages - My Page (User)
const CartPage = lazy(() => import('./pages/my-page/cart').then((m) => ({ default: m.CartPage })));
const PurchaseHistoryPage = lazy(() =>
  import('./pages/my-page/order-history').then((m) => ({ default: m.PurchaseHistoryPage })),
);
const OrderDetailPage = lazy(() =>
  import('./pages/my-page/order-history/detail').then((m) => ({ default: m.OrderDetailPage })),
);
const ProfileCheckPage = lazy(() =>
  import('./pages/my-page/profile-check').then((m) => ({ default: m.ProfileCheckPage })),
);
const ProfilePage = lazy(() => import('./pages/my-page/profile').then((m) => ({ default: m.ProfilePage })));
const MyOfflineLicensePage = lazy(() =>
  import('./pages/my-page/offline-license').then((m) => ({ default: m.MyOfflineLicensePage })),
);
const BusinessConversionPage = lazy(() =>
  import('./pages/my-page/business-conversion').then((m) => ({ default: m.BusinessConversionPage })),
);
const PurchasePage = lazy(() => import('./pages/my-page/purchase').then((m) => ({ default: m.PurchasePage })));
const RefundHistoryPage = lazy(() =>
  import('./pages/my-page/refund-history').then((m) => ({ default: m.RefundHistoryPage })),
);
const RefundDetailPage = lazy(() =>
  import('./pages/my-page/refund-history/detail').then((m) => ({ default: m.RefundDetailPage })),
);
const SellerRegistrationPage = lazy(() =>
  import('./pages/my-page/seller-registration').then((m) => ({ default: m.SellerRegistrationPage })),
);
const TokenHistoryPage = lazy(() =>
  import('./pages/my-page/token-history').then((m) => ({ default: m.TokenHistoryPage })),
);
const LicensePage = lazy(() => import('./pages/my-page/license').then((m) => ({ default: m.LicensePage })));
const DevicePage = lazy(() => import('./pages/my-page/device').then((m) => ({ default: m.DevicePage })));
const MyInquiriesPage = lazy(() =>
  import('./pages/my-page/my-inquiries').then((m) => ({ default: m.MyInquiriesPage })),
);
const MyAnalysisRequestsPage = lazy(() =>
  import('./pages/my-page/my-analysis-requests').then((m) => ({ default: m.MyAnalysisRequestsPage })),
);

// Lazy loaded pages - My Page - Seller
const ProductManagementPage = lazy(() =>
  import('./pages/seller/my-page/product-management').then((m) => ({ default: m.ProductManagementPage })),
);
const ProductAddPage = lazy(() =>
  import('./pages/seller/my-page/product-add').then((m) => ({ default: m.ProductAddPage })),
);
const ProductEditPage = lazy(() =>
  import('./pages/seller/my-page/product-edit').then((m) => ({ default: m.ProductEditPage })),
);
const QnaManagementPage = lazy(() =>
  import('./pages/seller/my-page/qna-management/product').then((m) => ({ default: m.QnaManagementPage })),
);
const RefundManagementPage = lazy(() =>
  import('./pages/seller/my-page/refund-management').then((m) => ({ default: m.RefundManagementPage })),
);
const RefundManagementDetailPage = lazy(() =>
  import('./pages/seller/my-page/refund-management/detail').then((m) => ({ default: m.RefundManagementDetailPage })),
);
const ReviewManagementPage = lazy(() =>
  import('./pages/seller/my-page/review-management/product').then((m) => ({ default: m.ReviewManagementPage })),
);
const SellerProfileEditPage = lazy(() =>
  import('./pages/seller/my-page/profile-edit').then((m) => ({ default: m.SellerProfileEditPage })),
);

// Lazy loaded pages - Payment
const PaymentSuccessPage = lazy(() =>
  import('./pages/payment/success').then((m) => ({ default: m.PaymentSuccessPage })),
);
const PaymentFailPage = lazy(() => import('./pages/payment/fail').then((m) => ({ default: m.PaymentFailPage })));
const LicensePaymentSuccessPage = lazy(() =>
  import('./pages/payment/license-success').then((m) => ({ default: m.LicensePaymentSuccessPage })),
);
const TokenPaymentSuccessPage = lazy(() =>
  import('./pages/payment/token-success').then((m) => ({ default: m.TokenPaymentSuccessPage })),
);

// Lazy loaded pages - Admin
const AdminPageLayout = lazy(() => import('./pages/admin').then((m) => ({ default: m.AdminPageLayout })));
const ExternalPointManagementPage = lazy(() =>
  import('./pages/admin/external-point-management').then((m) => ({ default: m.ExternalPointManagementPage })),
);
const ExternalPointFormPage = lazy(() =>
  import('./pages/admin/external-point-management/new').then((m) => ({ default: m.ExternalPointFormPage })),
);
const SellerApprovalPage = lazy(() =>
  import('./pages/admin/seller-approval').then((m) => ({ default: m.SellerApprovalPage })),
);
const ProductApprovalPage = lazy(() =>
  import('./pages/admin/product-approval').then((m) => ({ default: m.ProductApprovalPage })),
);
const CommunityAnalysisRequestListPage = lazy(() =>
  import('./pages/admin/analysis-request').then((m) => ({ default: m.CommunityAnalysisRequestListPage })),
);
const CommunityInquiryListPage = lazy(() =>
  import('./pages/admin/inquiry').then((m) => ({ default: m.CommunityInquiryListPage })),
);
const InquiryRedirectPage = lazy(() =>
  import('./pages/admin/inquiry/redirect').then((m) => ({ default: m.InquiryRedirectPage })),
);
const EducationManagementPage = lazy(() =>
  import('./pages/admin/education-management').then((m) => ({ default: m.EducationManagementPage })),
);
const EducationAdminCreatePage = lazy(() =>
  import('./pages/admin/education-management/new').then((m) => ({ default: m.EducationAdminCreatePage })),
);
const EducationAdminEditPage = lazy(() =>
  import('./pages/admin/education-management/edit').then((m) => ({ default: m.EducationAdminEditPage })),
);
const EducationAdminDetailPage = lazy(() =>
  import('./pages/admin/education-management/detail').then((m) => ({ default: m.EducationAdminDetailPage })),
);
const ClientManagementPage = lazy(() =>
  import('./pages/admin/client-management').then((m) => ({ default: m.ClientManagementPage })),
);
const ClientManagementCreatePage = lazy(() =>
  import('./pages/admin/client-management/new').then((m) => ({ default: m.ClientManagementCreatePage })),
);
const UserManagementPage = lazy(() =>
  import('./pages/admin/user-management').then((m) => ({ default: m.UserManagementPage })),
);
const UserDetailPage = lazy(() =>
  import('./pages/admin/user-management/[userId]').then((m) => ({ default: m.UserDetailPage })),
);
const FaqManagementPage = lazy(() =>
  import('./pages/admin/faq-management').then((m) => ({ default: m.FaqManagementPage })),
);
const TermManagementPage = lazy(() =>
  import('./pages/admin/term-management').then((m) => ({ default: m.TermManagementPage })),
);
const TermDetailPage = lazy(() =>
  import('./pages/admin/term-management/[id]').then((m) => ({ default: m.TermDetailPage })),
);
const TermCreatePage = lazy(() =>
  import('./pages/admin/term-management/new').then((m) => ({ default: m.TermCreatePage })),
);
const SellerTermManagementPage = lazy(() =>
  import('./pages/admin/seller-term-management').then((m) => ({ default: m.SellerTermManagementPage })),
);
const SellerTermDetailPage = lazy(() =>
  import('./pages/admin/seller-term-management/[id]').then((m) => ({ default: m.SellerTermDetailPage })),
);
const SellerTermCreatePage = lazy(() =>
  import('./pages/admin/seller-term-management/new').then((m) => ({ default: m.SellerTermCreatePage })),
);
const AnalyticsDashboardPage = lazy(() =>
  import('./pages/admin/analytics-dashboard').then((m) => ({ default: m.AnalyticsDashboardPage })),
);
const OfflineLicenseManagementPage = lazy(() =>
  import('./pages/admin/offline-license-management').then((m) => ({ default: m.OfflineLicenseManagementPage })),
);

// Error pages
const NotFound = lazy(() => import('./pages/error/not-found').then((m) => ({ default: m.NotFound })));

// Loading fallback component
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
    <div className="loading_spinner" />
  </div>
);

function AppContent() {
  useRouteChangeHandler();
  useExternalPointDetection();

  return (
    <div>
      <ToastContainer />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* 공통 레이아웃 라우트 */}
          <Route
            element={
              <CommonLayout>
                <Outlet />
              </CommonLayout>
            }
          >
            {/* Common */}
            <Route index element={<HomePage />} />
            <Route path={ROUTE_CONFIGS.SIGN_IN.path} element={<SignInPage />} />
            <Route path={ROUTE_CONFIGS.SIGN_UP.path} element={<SignUpPage />} />
            <Route path={ROUTE_CONFIGS.FIND_ID.path} element={<FindIdPage />} />
            <Route path={ROUTE_CONFIGS.RESET_PASSWORD.path} element={<ResetPasswordPage />} />
            <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
            <Route path="/sign-provision" element={<SignProvisionPage />} />
            <Route path="/sign-complete" element={<SignCompletePage />} />
            <Route path={ROUTE_CONFIGS.EYET_DOWNLOAD.path} element={<EyeTDownloadPage />} />
            <Route path={ROUTE_CONFIGS.ANALYSIS_REQUEST.path} element={<CommunityAnalysisRequestPage />} />
            <Route path={ROUTE_CONFIGS.INQUIRY.path} element={<CommunityInquiryPage />} />
            <Route path={ROUTE_CONFIGS.PRODUCT_INTRO.path} element={<ProductIntroPage />} />
            <Route path={ROUTE_CONFIGS.MARKETPLACE.path} element={<MarketPlacePage />} />
            <Route path={`${ROUTE_CONFIGS.PRODUCT.path}/:id`} element={<ProductDetailPage />} />
            <Route path={ROUTE_CONFIGS.EDUCATION_COURSE.path} element={<EducationCoursePage />} />
            <Route path="/service" element={<ServicePage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/expert-center" element={<ExpertCenterPage />} />
            <Route path="/external-login" element={<ExternalLoginPage />} />

            {/* Payment */}
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/fail" element={<PaymentFailPage />} />
            <Route path="/payment/license-success" element={<LicensePaymentSuccessPage />} />
            <Route path="/payment/token-success" element={<TokenPaymentSuccessPage />} />

            {/* 권한별 허용 메뉴 URL */}
            <Route element={<AuthRoute allowedRoles={['PERSONAL_USER', 'BUSINESS_USER', 'ADMIN']} />}>
              <Route path={ROUTE_CONFIGS.MY_PAGE.basePath} element={<MyPageLayout />}>
                <Route index element={<RefNavigate to={ROUTE_CONFIGS.MY_PAGE.routes.LICENSE.relativePath} replace />} />
                <Route
                  path={ROUTE_CONFIGS.MY_PAGE.routes.CART.relativePath}
                  element={<CartPage />}
                  handle={{
                    menuTitle: '장바구니',
                    title: '장바구니',
                    description: '선택한 상품의 목록과 결제 금액을 확인할 수 있습니다.',
                  }}
                />
                <Route
                  path={ROUTE_CONFIGS.MY_PAGE.routes.PURCHASE.relativePath}
                  element={<PurchasePage />}
                  handle={{ menuTitle: '구매', title: '구매' }}
                />
                <Route
                  path={ROUTE_CONFIGS.MY_PAGE.routes.DEVICE.relativePath}
                  element={<DevicePage />}
                  handle={{
                    menuTitle: '기기 관리',
                    title: '기기 관리',
                    description: '등록된 기기들을 관리하고 모니터링할 수 있습니다.',
                  }}
                />
                <Route
                  path={ROUTE_CONFIGS.MY_PAGE.routes.ORDER_HISTORY.relativePath}
                  element={<PurchaseHistoryPage />}
                />
                <Route
                  path={`${ROUTE_CONFIGS.MY_PAGE.routes.ORDER_HISTORY.relativePath}/:orderId`}
                  element={<OrderDetailPage />}
                />
                <Route
                  path={ROUTE_CONFIGS.MY_PAGE.routes.REFUND_HISTORY.relativePath}
                  element={<RefundHistoryPage />}
                />
                <Route
                  path={`${ROUTE_CONFIGS.MY_PAGE.routes.REFUND_HISTORY.relativePath}/:orderItemId`}
                  element={<RefundDetailPage />}
                />
                <Route path={ROUTE_CONFIGS.MY_PAGE.routes.TOKEN_HISTORY.relativePath} element={<TokenHistoryPage />} />
                <Route path={ROUTE_CONFIGS.MY_PAGE.routes.LICENSE.relativePath} element={<LicensePage />} />
                <Route path={ROUTE_CONFIGS.MY_PAGE.routes.MY_INQUIRIES.relativePath} element={<MyInquiriesPage />} />
                <Route
                  path={ROUTE_CONFIGS.MY_PAGE.routes.MY_ANALYSIS_REQUESTS.relativePath}
                  element={<MyAnalysisRequestsPage />}
                />
                <Route path={ROUTE_CONFIGS.MY_PAGE.routes.PROFILE_CHECK.relativePath} element={<ProfileCheckPage />} />
                <Route
                  path={ROUTE_CONFIGS.MY_PAGE.routes.PROFILE.relativePath}
                  element={<ProfilePage />}
                  handle={{ menuTitle: '회원정보', title: '회원정보' }}
                />
                <Route
                  path={ROUTE_CONFIGS.MY_PAGE.routes.OFFLINE_LICENSE.relativePath}
                  element={<MyOfflineLicensePage />}
                  handle={{
                    menuTitle: ROUTE_CONFIGS.MY_PAGE.routes.OFFLINE_LICENSE.title,
                    title: ROUTE_CONFIGS.MY_PAGE.routes.OFFLINE_LICENSE.title,
                    description: ROUTE_CONFIGS.MY_PAGE.routes.OFFLINE_LICENSE.description,
                  }}
                />
                <Route
                  path={ROUTE_CONFIGS.MY_PAGE.routes.BUSINESS_CONVERSION.relativePath}
                  element={<BusinessConversionPage />}
                  handle={{
                    menuTitle: ROUTE_CONFIGS.MY_PAGE.routes.BUSINESS_CONVERSION.title,
                    title: ROUTE_CONFIGS.MY_PAGE.routes.BUSINESS_CONVERSION.title,
                    description: ROUTE_CONFIGS.MY_PAGE.routes.BUSINESS_CONVERSION.description,
                  }}
                />
                <Route
                  path={ROUTE_CONFIGS.MY_PAGE.routes.SELLER_REGISTRATION.relativePath}
                  element={<SellerRegistrationPage />}
                />
              </Route>
            </Route>

            <Route element={<AuthRoute allowedRoles={['PERSONAL_USER', 'BUSINESS_USER', 'ADMIN']} />}>
              <Route path={ROUTE_CONFIGS.SELLER_MY_PAGE.basePath} element={<MyPageLayout />}>
                <Route
                  index
                  element={
                    <RefNavigate to={ROUTE_CONFIGS.SELLER_MY_PAGE.routes.PRODUCT_MANAGEMENT.relativePath} replace />
                  }
                />
                <Route path="profile-edit" element={<SellerProfileEditPage />} />
                <Route
                  path={ROUTE_CONFIGS.SELLER_MY_PAGE.routes.PRODUCT_MANAGEMENT.relativePath}
                  element={<ProductManagementPage />}
                />
                <Route
                  path={ROUTE_CONFIGS.SELLER_MY_PAGE.routes.PRODUCT_ADD.relativePath}
                  element={<ProductAddPage />}
                />
                <Route path="product-edit/:productId" element={<ProductEditPage />} />
                <Route path="qna-management/product/:productId" element={<QnaManagementPage />} />
                <Route path="review-management/product/:productId" element={<ReviewManagementPage />} />
                <Route
                  path={ROUTE_CONFIGS.SELLER_MY_PAGE.routes.REFUND_MANAGEMENT.relativePath}
                  element={<RefundManagementPage />}
                />
                <Route path="refund-management/:productId" element={<RefundManagementDetailPage />} />
              </Route>
            </Route>
          </Route>

          {/* Admin 라우트 */}
          <Route element={<AuthRoute allowedRoles={['ADMIN']} />}>
            <Route path="/admin" element={<AdminPageLayout />}>
              <Route index element={<Navigate to="analytics-dashboard" replace />} />
              <Route path="seller-approval" element={<SellerApprovalPage />} />
              <Route path="product-approval" element={<ProductApprovalPage />} />
              <Route path="analysis-request" element={<CommunityAnalysisRequestListPage />} />
              <Route path="inquiry" element={<CommunityInquiryListPage />} />
              <Route path="education-management" element={<EducationManagementPage />} />
              <Route path="education-management/new" element={<EducationAdminCreatePage />} />
              <Route path="education-management/:id/edit" element={<EducationAdminEditPage />} />
              <Route path="education-management/:id" element={<EducationAdminDetailPage />} />
              <Route path="client-management" element={<ClientManagementPage />} />
              <Route path="client-management/new" element={<ClientManagementCreatePage />} />
              <Route path="client-management/:id/edit" element={<ClientManagementCreatePage />} />
              <Route path="user-management" element={<UserManagementPage />} />
              <Route path="user-management/:userId" element={<UserDetailPage />} />
              <Route path="offline-license-management" element={<OfflineLicenseManagementPage />} />
              <Route path="faq-management" element={<FaqManagementPage />} />
              <Route path="term-management" element={<TermManagementPage />} />
              <Route path="term-management/new" element={<TermCreatePage />} />
              <Route path="term-management/:id" element={<TermDetailPage />} />
              <Route path="seller-term-management" element={<SellerTermManagementPage />} />
              <Route path="seller-term-management/new" element={<SellerTermCreatePage />} />
              <Route path="seller-term-management/:id" element={<SellerTermDetailPage />} />
              <Route path="analytics-dashboard" element={<AnalyticsDashboardPage />} />
              <Route path="external-point-management" element={<ExternalPointManagementPage />} />
              <Route path="external-point-management/new" element={<ExternalPointFormPage />} />
              <Route path="external-point-management/:id/edit" element={<ExternalPointFormPage />} />
            </Route>
          </Route>

          {/* Admin inquiries 상세 - 일반 사용자 리다이렉트 */}
          <Route element={<AuthRoute allowedRoles={['PERSONAL_USER', 'BUSINESS_USER', 'ADMIN']} />}>
            <Route path="/admin/inquiries/:id" element={<InquiryRedirectPage />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <SpinnerProvider>
          <AppContent />
        </SpinnerProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
